import { useAuth0 } from '@auth0/auth0-react';
import FuzzySearch from 'fuzzy-search';
import React, { useCallback, useEffect, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { BibleStudy } from '../../../api/models/BibleStudy';
import { bibleStudySlice } from '../../../model/bibleStudySlice';
import { RootState } from '../../../model/store';
import { routes } from '../../../route-list';
import { reorder } from '../../../util/reorder';
import { useRequest } from '../../../util/use-request';
import { RhTab } from '../../core/RhTabs';
import { BibleStudiesScreen } from '../components/BibleStudiesScreen';

export type BibleStudiesProps = {
  // Inputs
  /** current tab id on the uri */
  currentTabId: string;
  /** tabs of this view */
  tabs: RhTab[];
  bibleStudies: BibleStudy[] | null;
  isLoading: boolean;
  isMyStudiesOnly: boolean;
  isEditingOrder: boolean;
  searchTerm: string;
  // Inputs
  userClickedEditOrder: () => void;
  /** called when the user clicks a tab */
  userSelectedTab: (id: string) => void;
  /** navigates to add bible study route */
  userClickedAdd: () => void;
  /** navigates to view single bible study route */
  userClickedAStudy: (id: string) => void;
  /** called when the user updates the search field */
  userUpdatedSearchTerm: (searchTerm: string) => void;
  userClickedMyStudies: () => void;
  userToggledFeatureStatus: (id: string, payload: { isFeatured: boolean }) => void;
  userEndedDrag: (result: DropResult) => void;
  userClickedSaveOrder: () => void;
  userCanceledReorder: () => void;
};

export const BibleStudiesContainer = () => {
  const bibleStudies = useSelector((state: RootState) => state.bibleStudy.list);
  const { user } = useAuth0();

  const [currentTabId, setCurrentTabId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isGettingPrograms, setIsGettingPrograms] = useState(false);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [myStudies, setMyStudies] = useState<BibleStudy[] | null>(null);

  const request = useRequest();
  const history = useHistory();
  const dispatch = useDispatch();

  const loadBibleStudies = useCallback(
    async (params?: { isFeatured?: boolean; showOnlyFromCurrentUser?: boolean }) => {
      try {
        setIsGettingPrograms(true);
        const bibleStudies = await request<BibleStudy[]>(apiConfigs.getBibleStudies(params));
        dispatch(bibleStudySlice.actions.receivedList({ bibleStudies }));
      } catch (err: any) {
        toast.error(err.message || 'Failed to load studies!');
      } finally {
        setIsGettingPrograms(false);
      }
    },
    [dispatch, request]
  );

  // Initially load studies
  useEffect(() => {
    loadBibleStudies({ showOnlyFromCurrentUser: true });
  }, []);

  const searcher = new FuzzySearch(myStudies || bibleStudies, [
    'publishingStatus',
    'description',
    'dateCreated',
    'authorName',
    'title',
  ]);

  return (
    <BibleStudiesScreen
      tabs={[
        { id: 'all', title: 'All' },
        { id: 'featured-studies', title: 'Featured Studies' },
      ]}
      currentTabId={currentTabId}
      bibleStudies={searcher.search(searchTerm)}
      isLoading={isGettingPrograms || isUpdating}
      isMyStudiesOnly={!!myStudies}
      isEditingOrder={isEditingOrder}
      searchTerm={searchTerm}
      userClickedEditOrder={() => {
        setSearchTerm('');
        setIsEditingOrder(true);
        // setMyStudies([]);
      }}
      userUpdatedSearchTerm={setSearchTerm}
      userClickedAdd={() => {
        history.push(routes.BIBLE_STUDIES__ADD);
      }}
      userSelectedTab={id => {
        if (currentTabId !== id) {
          setCurrentTabId(id);
          loadBibleStudies(id === 'featured-studies' ? { isFeatured: true, showOnlyFromCurrentUser: true } : undefined);
        }
      }}
      userClickedAStudy={id => {
        history.push(routes.BIBLE_STUDIES__VIEW(id));
      }}
      userClickedMyStudies={() => {
        if (!myStudies) {
          const newStudies = bibleStudies.filter(study => study.author === user?.sub);
          if (!newStudies.length) {
            toast.info("You don't have any studies yet!");
          }
          setMyStudies(newStudies);
        } else {
          setMyStudies(null);
        }
      }}
      userToggledFeatureStatus={async (id, { isFeatured }) => {
        try {
          setIsUpdating(true);
          const programIndex = bibleStudies.findIndex(study => study.id === id);
          if (programIndex === -1) throw new Error('Program does not exist');

          const program = bibleStudies[programIndex];
          const payload = { ...program, isFeatured };
          await request(apiConfigs.putBibleStudy(payload));

          loadBibleStudies(
            currentTabId === 'featured-studies' ? { isFeatured: true, showOnlyFromCurrentUser: true } : undefined
          );

          if (isFeatured) {
            toast.success(`${program.title} added to Featured!`);
          } else {
            toast.success(`${program.title} removed from Featured!`);
          }
        } catch (e: any) {
          toast.error(e.message || 'Failed to add to featured');
        } finally {
          setIsUpdating(false);
        }
      }}
      userEndedDrag={result => {
        // dropped outside the list
        if (!result.destination || !bibleStudies?.length) {
          return;
        }
        const newBibleStudies = reorder(bibleStudies, result.source.index, result.destination.index);
        dispatch(bibleStudySlice.actions.receivedList({ bibleStudies: newBibleStudies }));
      }}
      userClickedSaveOrder={async () => {
        setIsEditingOrder(false);
        setIsUpdating(true);

        const newFeaturedStudies = [...bibleStudies];
        const requests = newFeaturedStudies.map((study, i) => {
          return request(apiConfigs.putBibleStudy({ ...study, sortOrder: i }));
        });
        const results = await Promise.allSettled(requests);
        const failed = results.some(item => item.status === 'rejected');
        setIsUpdating(false);
        if (failed) {
          toast.error('There are items that were not updated!');
        } else {
          toast.success('Successfully updated featured studies!');
        }
      }}
      userCanceledReorder={() => {
        setIsEditingOrder(false);
      }}
    />
  );
};
